import type { PlatformPost, SocialMediaReport } from "../hooks/useSocialMediaReport";
import { MONTH_SHORT } from "./metricChange";

// The keys the backend sums into a post's engagement (ENGAGEMENT_METRICS).
const ENGAGEMENT_KEYS: Record<"facebook" | "instagram", readonly string[]> = {
  facebook: ["reactions", "comments", "shares"],
  instagram: ["likes", "comments", "saved"],
};

type SocialPlatform = keyof typeof ENGAGEMENT_KEYS;

const METRIC_LABEL: Record<string, string> = {
  reactions: "Reactions",
  comments: "Comments",
  shares: "Shares",
  likes: "Likes",
  saved: "Saves",
};

/** One platform's side of a post: its own time, link and counts. */
export interface DayPostPlatform {
  platform: SocialPlatform;
  publishedAt: string;
  permalink: string | null;
  /** The counts its engagement sums, labelled: "Reactions", "Saves". */
  metrics: Array<{ label: string; value: number }>;
  engagement: number;
}

/** The Meta campaign that promoted a post in the month. */
export interface PostBoost {
  campaignName: string;
  startDate: string;
  /** Null while it runs. */
  endDate: string | null;
  /** The month's spend from the daily rows; null with none. */
  spend: number | null;
}

/** One post as a chart pip's card shows it. A post that went out on
 *  Facebook and Instagram on the same day is one entry with both. */
export interface DayPost {
  key: string;
  /** TouchPoint's post, when the report matched one. */
  postId: string | null;
  platforms: string[];
  /** The earliest platform's publish time, ISO. */
  publishedAt: string;
  thumbnailUrl: string | null;
  caption: string | null;
  /** Lifetime engagement summed over the platforms, as the report read it. */
  engagement: number;
  /** Each platform it went out on, in the order of `platforms`. */
  perPlatform: DayPostPlatform[];
  boost?: PostBoost;
}

export interface PostDay {
  /** Day of the month, in Europe/Rome. */
  day: number;
  posts: DayPost[];
}

/** A day a paid campaign started or ended. */
export interface CampaignEvent {
  /** The Rome calendar day, "YYYY-MM-DD". */
  date: string;
  kind: "started" | "ended";
  campaignName: string;
  /** Google's channel ("SEARCH", "PERFORMANCE_MAX"); null on Meta. */
  channelType: string | null;
  /** Meta's FACEBOOK or INSTAGRAM; absent on Google. */
  platform?: string;
}

export interface CampaignDay {
  /** Day of the month, in Europe/Rome. */
  day: number;
  events: CampaignEvent[];
}

/** What the markers read of a campaign, Meta's or Google's. */
export interface CampaignSpan {
  key: string;
  name: string;
  /** "YYYY-MM-DD"; null when unknown, read then from the first daily row. */
  startDate: string | null;
  /** Null while it runs. */
  endDate: string | null;
  /** Days with a daily row, ascending. */
  days: ReadonlyArray<string>;
  channelType: string | null;
  platform?: string;
}

/** Where a campaign starts: its start date, else its first daily row. */
const startOf = (campaign: CampaignSpan): string | null => campaign.startDate ?? campaign.days[0] ?? null;

/** The starts and ends of the campaigns in any month (campaignsByDay keeps
 *  the month's). A running campaign has no end, one with no known start
 *  no start. */
export const campaignEvents = (campaigns: ReadonlyArray<CampaignSpan>): CampaignEvent[] =>
  campaigns.flatMap((campaign) => {
    const start = startOf(campaign);
    const base = {
      campaignName: campaign.name,
      channelType: campaign.channelType,
      ...(campaign.platform ? { platform: campaign.platform } : {}),
    };
    return [
      ...(start ? [{ ...base, date: start, kind: "started" as const }] : []),
      ...(campaign.endDate ? [{ ...base, date: campaign.endDate, kind: "ended" as const }] : []),
    ];
  });

/** A month's campaign starts and ends grouped by day, days ascending and
 *  each day's events in the order given. The backend sends Rome days, so
 *  the date string is read as is. */
export const campaignsByDay = (
  events: ReadonlyArray<CampaignEvent>,
  year: number,
  month: number,
): CampaignDay[] => {
  const monthPrefix = `${year}-${String(month).padStart(2, "0")}-`;
  const days = new Map<number, CampaignEvent[]>();
  events.forEach((event) => {
    if (!event.date.startsWith(monthPrefix)) return;
    const day = Number(event.date.slice(8, 10));
    days.set(day, [...(days.get(day) ?? []), event]);
  });
  return Array.from(days, ([day, dayEvents]) => ({ day, events: dayEvents })).sort(
    (a, b) => a.day - b.day,
  );
};

const ROME_DATE = new Intl.DateTimeFormat("en-CA", {
  timeZone: "Europe/Rome",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

// Graph sends "+0000", which not every engine reads, so the offset gains
// its colon first.
export const parseGraphTime = (time: string): number =>
  Date.parse(time.replace(/([+-]\d{2})(\d{2})$/, "$1:$2"));

/** The Europe/Rome calendar day of a Graph API time, "YYYY-MM-DD", or null
 *  when it does not parse. */
export const romeDate = (time: string): string | null => {
  const ms = parseGraphTime(time);
  return Number.isNaN(ms) ? null : ROME_DATE.format(ms);
};

const platformSide = (platform: SocialPlatform, post: PlatformPost, time: string): DayPostPlatform => {
  const counts: Partial<Record<string, number>> = { ...post.metrics };
  const metrics = ENGAGEMENT_KEYS[platform].map((key) => ({
    label: METRIC_LABEL[key],
    value: counts[key] ?? 0,
  }));
  return {
    platform,
    publishedAt: time,
    permalink: post.permalink ?? null,
    metrics,
    engagement: metrics.reduce((sum, m) => sum + m.value, 0),
  };
};

// A video's media_url is the video itself, so it never serves as the image.
const thumbnailOf = (post: PlatformPost): string | null =>
  post.thumbnailUrl ??
  post.imageUrl ??
  (post.mediaType !== "VIDEO" ? post.mediaUrl : undefined) ??
  null;

/** The report's posts grouped by the Rome day they went out, for the pips on
 *  a day-axis chart of the same month. Days ascend, a day's posts by time. */
export const postsByDay = (
  report: Pick<SocialMediaReport, "facebook" | "instagram">,
  year: number,
  month: number,
): PostDay[] => {
  const monthPrefix = `${year}-${String(month).padStart(2, "0")}-`;
  const byKey = new Map<string, DayPost & { day: number; ms: number }>();
  (Object.keys(ENGAGEMENT_KEYS) as SocialPlatform[]).forEach((platform) => {
    report[platform]?.allPosts?.forEach((post) => {
      const time = platform === "facebook" ? post.createdTime : post.timestamp;
      if (!time) return;
      const date = romeDate(time);
      if (!date || !date.startsWith(monthPrefix)) return;
      const ms = parseGraphTime(time);
      const day = Number(date.slice(8, 10));
      const key = `${day}|${post.internal?.postId ?? `${platform}:${post.id}`}`;
      const entry = byKey.get(key);
      const side = platformSide(platform, post, time);
      if (!entry) {
        byKey.set(key, {
          key,
          postId: post.internal?.postId ?? null,
          day,
          ms,
          platforms: [platform],
          publishedAt: time,
          thumbnailUrl: thumbnailOf(post),
          caption: post.message || post.caption || null,
          engagement: side.engagement,
          perPlatform: [side],
        });
        return;
      }
      entry.platforms.push(platform);
      entry.perPlatform.push(side);
      if (ms < entry.ms) Object.assign(entry, { ms, publishedAt: time });
      entry.engagement += side.engagement;
      entry.thumbnailUrl = entry.thumbnailUrl ?? thumbnailOf(post);
      entry.caption = entry.caption ?? (post.message || post.caption || null);
    });
  });

  const days = new Map<number, DayPost[]>();
  Array.from(byKey.values())
    .sort((a, b) => a.ms - b.ms)
    .forEach(({ day, ms, ...post }) => days.set(day, [...(days.get(day) ?? []), post]));
  return Array.from(days, ([day, posts]) => ({ day, posts })).sort((a, b) => a.day - b.day);
};

/** The boosted posts marked: a post a Meta campaign of the month promoted
 *  (its postId the post's) carries that campaign, or with several the span
 *  from the first start to the last end and their spend added. */
export const markBoosted = (
  days: ReadonlyArray<PostDay>,
  campaigns: ReadonlyArray<{
    name: string;
    postId: string | null;
    startDate: string;
    endDate: string | null;
    daily: ReadonlyArray<{ spend: number }>;
  }>,
): PostDay[] => {
  const byPost = new Map<string, PostBoost>();
  campaigns.forEach((campaign) => {
    if (!campaign.postId) return;
    const spend = campaign.daily.length > 0 ? campaign.daily.reduce((sum, d) => sum + d.spend, 0) : null;
    const seen = byPost.get(campaign.postId);
    if (!seen) {
      byPost.set(campaign.postId, {
        campaignName: campaign.name,
        startDate: campaign.startDate,
        endDate: campaign.endDate,
        spend,
      });
      return;
    }
    byPost.set(campaign.postId, {
      campaignName: seen.campaignName,
      startDate: campaign.startDate < seen.startDate ? campaign.startDate : seen.startDate,
      endDate:
        seen.endDate == null || campaign.endDate == null
          ? null
          : campaign.endDate > seen.endDate
          ? campaign.endDate
          : seen.endDate,
      spend: seen.spend == null && spend == null ? null : (seen.spend ?? 0) + (spend ?? 0),
    });
  });
  if (byPost.size === 0) return [...days];
  return days.map((day) => ({
    ...day,
    posts: day.posts.map((post) => {
      const boost = post.postId ? byPost.get(post.postId) : undefined;
      return boost ? { ...post, boost } : post;
    }),
  }));
};

/** "3 Oct" from "2026-10-03", read from the string so no time zone moves it. */
export const formatShortDay = (isoDate: string): string => {
  const [, month, day] = isoDate.slice(0, 10).split("-").map(Number);
  return `${day} ${MONTH_SHORT[month - 1]}`;
};

/** A campaign's days: "3–17 Oct", "28 Sep – 4 Oct", or "since 3 Oct" while
 *  it runs. */
export const formatCampaignDates = (startDate: string, endDate: string | null): string => {
  if (!endDate) return `since ${formatShortDay(startDate)}`;
  if (startDate === endDate) return formatShortDay(startDate);
  if (startDate.slice(0, 7) === endDate.slice(0, 7)) {
    return `${Number(startDate.slice(8, 10))}–${formatShortDay(endDate)}`;
  }
  return `${formatShortDay(startDate)} – ${formatShortDay(endDate)}`;
};

/** One campaign's lane under a day chart, in the month's days. */
export interface CampaignLane {
  key: string;
  name: string;
  channelType: string | null;
  startDate: string;
  endDate: string | null;
  from: number;
  to: number;
  /** The start or end falls inside the month: that end of the bar is a pip. */
  startsHere: boolean;
  endsHere: boolean;
}

/** The lanes of a month's campaigns: the first `max` of them as given (the
 *  caller ranks them), drawn oldest first. Each is clipped to the month; one
 *  still running runs to `settledDay`, the chart's last settled day. */
export const campaignLanes = (
  campaigns: ReadonlyArray<CampaignSpan>,
  year: number,
  month: number,
  settledDay: number,
  max = 3,
): { lanes: CampaignLane[]; hidden: number } => {
  const pad = (n: number) => String(n).padStart(2, "0");
  const lastDay = new Date(year, month, 0).getDate();
  const first = `${year}-${pad(month)}-01`;
  const last = `${year}-${pad(month)}-${pad(lastDay)}`;
  const dayOf = (iso: string) => Number(iso.slice(8, 10));
  const lanes = campaigns.flatMap((campaign): CampaignLane[] => {
    const start = startOf(campaign);
    const end = campaign.endDate;
    if (!start || start > last || (end != null && end < first)) return [];
    const startsHere = start >= first;
    const from = startsHere ? dayOf(start) : 1;
    const endsHere = end != null && end <= last;
    const to =
      end == null ? Math.min(Math.max(settledDay, from), lastDay) : endsHere ? dayOf(end) : lastDay;
    return [
      {
        key: campaign.key,
        name: campaign.name,
        channelType: campaign.channelType,
        startDate: start,
        endDate: end,
        from,
        to,
        startsHere,
        endsHere,
      },
    ];
  });
  const shown = lanes
    .slice(0, max)
    .sort((a, b) => a.startDate.localeCompare(b.startDate) || a.name.localeCompare(b.name));
  return { lanes: shown, hidden: lanes.length - shown.length };
};
